import React, { createContext, useMemo, useState } from 'react';
import {
  useAffiliateLookup,
  AffiliateLookupPayload,
} from '@agria/paws/src/hooks/useAffiliateLookup';
import { UseFormReturn, useForm, UseMutateFunction } from 'react-hook-form';
import { Affiliate } from '@agria/paws/src/types';
import { isBrowser } from '@agria/utils';
import { useAffiliateDetails } from '../hooks/useAffiliateDetails';
import { AffiliateLoginFormData } from '../types/login';
import { validateAffiliateLogin } from '../utils/validateAffiliateLogin';

interface AuthContextProps {
  id: string | null;
  loginUser: UseMutateFunction<
    Affiliate,
    unknown,
    AffiliateLookupPayload,
    unknown
  >;
  loginError: boolean;
  loginLoading: boolean;
  loginData: Affiliate | undefined;
  formMethods: UseFormReturn<AffiliateLoginFormData>;
  onSubmit: (data: AffiliateLoginFormData) => void;
  affiliateTypeError: boolean;
  invalidStatusIdError: boolean;
}
interface AuthContextProviderProps {
  children: React.ReactNode;
}

export const AuthContext = createContext({} as AuthContextProps);
AuthContext.displayName = 'AuthContext';

const AFFILIATE_ID_STORAGE_KEY = 'Agria@AffiliateId';

export const AuthProvider = ({ children }: AuthContextProviderProps) => {
  const {
    mutate: loginUser,
    data: loginData,
    isError: loginError,
    isPending: loginLoading,
  } = useAffiliateLookup();

  const [affiliateTypeError, setAffiliateTypeError] = useState(false);
  const [invalidStatusIdError, setInvalidStatusIdError] = useState(false);

  const defaultId = isBrowser
    ? window.sessionStorage.getItem(AFFILIATE_ID_STORAGE_KEY)
    : '';
  const [id, setId] = useState<string | null>(defaultId);

  const { affiliateTypeIds, affiliateType } = useAffiliateDetails();

  // const formMethods = useForm({
  //   mode: 'onBlur',
  // });
  const formMethods = useForm<AffiliateLoginFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { formState } = formMethods;
  const { isValid } = formState;

  const returnValues = useMemo(() => {
    const onSubmit = (formData: AffiliateLoginFormData) => {
      if (!isValid) return;
      const payLoad: AffiliateLookupPayload = {};

      if (formData.email) {
        payLoad.email = formData.email;
      }
      if (formData.agriaId) {
        payLoad.agriaId = formData.agriaId;
      }
      if (formData.postcode) {
        payLoad.postcode = formData.postcode;
      }
      // Regex to see if postcodeOrId is a number, then we need to set agriaId else need to set postcode
      if (formData.postcodeOrId) {
        const numberRegEx = /^[0-9]+$/;
        if (numberRegEx.test(formData.postcodeOrId as string)) {
          payLoad.agriaId = formData.postcodeOrId;
        } else {
          payLoad.postcode = formData.postcodeOrId;
        }
      }
      setAffiliateTypeError(false);
      setInvalidStatusIdError(false);

      loginUser(payLoad, {
        onSuccess: (loginResponseData) => {
          const validationResult = validateAffiliateLogin({
            affiliate: loginResponseData,
            affiliateType,
            affiliateTypeIds,
          });

          if (!validationResult.isAllowed) {
            setAffiliateTypeError(validationResult.affiliateTypeError);
            setInvalidStatusIdError(validationResult.invalidStatusIdError);
            setId(null);
            window.sessionStorage.removeItem(AFFILIATE_ID_STORAGE_KEY);
            return;
          }

          setId(loginResponseData.Id);
          window.sessionStorage.setItem(
            AFFILIATE_ID_STORAGE_KEY,
            loginResponseData.Id
          );
        },
      });
    };

    return {
      loginError: loginError || affiliateTypeError || invalidStatusIdError,
      loginUser,
      loginData,
      formMethods,
      onSubmit,
      loginLoading,
      id,
      affiliateTypeError,
      invalidStatusIdError,
    };
  }, [
    loginError,
    affiliateTypeError,
    invalidStatusIdError,
    loginUser,
    loginData,
    formMethods,
    loginLoading,
    id,
    isValid,
    affiliateType,
    affiliateTypeIds,
  ]);

  return (
    <AuthContext.Provider value={returnValues}>{children}</AuthContext.Provider>
  );
};
